import { useCallback, useEffect, useMemo, useState } from 'react';
import { StreamChat } from 'stream-chat';
import get from 'lodash/get';
import filter from 'lodash/filter';
import find from 'lodash/find';
import includes from 'lodash/includes';
import map from 'lodash/map';

import { useLoading } from 'shared-components/utils/hooks/use-loading';

import { Messages } from '../../../../api/api-messages';
import { useStreamChannelListener } from './use-stream-listener';
import { streamErrors } from './utils';

const MESSAGE_LIMIT = 101;

export const useStreamChat = (
  channelId,
  streamApiKey,
  profile,
  accessToken,
  generateMessageMetadata,
  onSystemMessageReceived,
  intl
) => {
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [inputErrors, setInputErrors] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const { SSOid } = profile?.data || {};

  const streamUser = useMemo(() => {
    if (!profile.data) {
      return null;
    }
    const {
      first_name,
      last_name,
      avatar_url,
      avatar_id,
      SSOid
    } = profile?.data || {};

    const formattedLastName = `${last_name[0]}.`;

    return {
      id: SSOid,
      image: avatar_url,
      avatar_url,
      avatar_id,
      first_name,
      last_name: formattedLastName,
      name: `${first_name} ${formattedLastName}`
    };
  }, [profile]);

  const getStreamToken = useCallback(async () => {
    if (SSOid && accessToken) {
      return new Promise((resolve) => {
        Messages.readStreamToken(SSOid, accessToken, (response) => {
          if (200 === response.statusCode) {
            resolve(response.stream_user_token);
          }
        });
      });
    }
  }, [SSOid, accessToken]);

  const createWebSocket = async () => {
    try {
      const chatClient = StreamChat.getInstance(streamApiKey);
      await chatClient.connectUser(streamUser, getStreamToken);
      setClient(chatClient);
      setIsConnected(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const { asyncFn: connect, isLoading: isConnecting } = useLoading(createWebSocket);

  useEffect(() => {
    if (isConnected || !streamUser || !streamApiKey) {
      return;
    }
    connect();
  }, [isConnected, streamUser, streamApiKey]);

  const { asyncFn: getActiveChannel, isLoading: isFetchingChannel } = useLoading(async () => {
    try {
      const sort = { last_message_at: -1 };
      const filter = {
        cid: `messaging:${channelId}`
      };
      const channels = await client.queryChannels(filter, sort, {
        watch: true,
        state: true,
        message_limit: MESSAGE_LIMIT
      });
      if (channels && channels[0]) {
        setActiveChannel(channels[0]);
      }
    } catch (e) {
      setError(e);
    }
  });

  useEffect(() => {
    if (isConnected) {
      getActiveChannel();
    }
  }, [isConnected]);

  const closeWebSocket = async () => {
    if (client) {
      try {
        await client.disconnectUser();
        setClient(null);
        setIsConnected(false);
      } catch (e) {
        setError(e.message);
      }
    }
  };

  // If the tab becomes inactive for 5 seconds disconnect the user
  // Reconnect the user if the tab becomes active again
  useEffect(() => {
    if (!client) return;

    let timeoutId;

    const handleVisibilityChange = () => {
      if ('hidden' === document.visibilityState) {
        timeoutId = setTimeout(() => {
          client.disconnectUser();
          setIsConnected(false);
        }, 5_000);
      } else {
        clearTimeout(timeoutId);

        if (!isConnected) {
          client.connectUser(streamUser, getStreamToken);
          setIsConnected(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [client]);

  // Add stream_connection_id to session storage for automation tests
  useEffect(() => {
    if (client && isConnected) {
      sessionStorage.setItem('stream_connection_id', client?._getConnectionID?.());
    }
  }, [client, isConnected]);

  useEffect(() => {
    return () => {
      if (isConnected) {
        closeWebSocket();
      }
    };
  }, [isConnected]);

  // Listen for system messages and fetch booking info
  useStreamChannelListener(activeChannel, 'message.new', (event) => {
    const messageType = get(event, 'message.type');
    if ('system' === messageType) {
      onSystemMessageReceived();
    }
  });

  const addInputErrorIds = useCallback((newErrors = []) => {
    const errors = map(newErrors, (newError) => {
      const { id: errorId, values, customMessage } = newError;

      if (customMessage) {
        return { message: customMessage, id: errorId };
      }

      const errorMessageMeta = get(streamErrors, errorId);
      if (!errorMessageMeta) {
        return;
      }

      const errorIdStateDuplicate = find(inputErrors, (error = {}) => error.id === errorId);
      const errorIdDuplicate = 1 < filter(newErrors, (e = {}) => e.id === errorId).length;
      if (errorIdStateDuplicate || errorIdDuplicate) {
        return;
      }

      const message = intl.formatMessage({ ...errorMessageMeta }, { ...values });
      return { message, id: errorId };
    });

    setInputErrors((prev = []) => [...prev, ...filter(errors)]);
  }, [inputErrors]);

  const removeInputErrorIds = useCallback((errorIds = []) => {
    const filtered = filter(inputErrors, (error = {}) => !includes(errorIds, error.id));
    setInputErrors(filtered);
  }, [inputErrors]);

  const clearInputErrors = useCallback(() => {
    setInputErrors([]);
  }, [inputErrors]);

  return {
    client,
    error,
    activeChannel,
    isConnected,
    isConnecting,
    addInputErrorIds,
    removeInputErrorIds,
    clearInputErrors,
    inputErrors,
    isFetchingChannel
  };
};
